import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import { 
  Box, 
  Button, 
  Stack,
  useToast,
} from '@chakra-ui/react'
import { MdFiberNew } from 'react-icons/md'

import { Helmet } from 'react-helmet-async'

import JourneyMap from '../components/JourneysMap'
import JourneyOverview from '../components/JourneyOverview'
import JourneyTable from '../components/JourneyTable'
import JourneyForm from '../components/JourneyForm'
import JourneyFilter from '../components/JourneyFilter'

import { getJourneys, reset } from '../features/journeys/journeySlice'
import { getUserObject, logoutUser, reset as resetAuth } from '../features/auth/authSlice'

import LoadingOverlay from '../components/LoadingOverlay'
import StatusOverlay from '../components/StatusOverlay'
import MapWithSidebarLayout from '../components/MapWithSidebarLayout'
import ViewInsightsButton from '../components/ui/ViewInsightsButton'
import SubscriptionExpiredBanner from './page_elements/SubscriptionExpiredBanner'
import { hasLimitedValiditySubscription } from '../utilities/journeyCreateHelper'
import OnboardingViews from './page_elements/OnboardingViews'


function MyJourneys() {
  
  const [journeysToDisplay, setJourneysToDisplay] = useState([])
  const [previewPoints, setPreviewPoints] = useState({})

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const toast = useToast()

  const { user } = useSelector((state) => state.auth)

  const { journeys, isSuccess, isError, isLoading, actionType, message } = useSelector((state) => state.journeys)

  useEffect(() => {

    if (!user) {
      navigate('/login')
      return
    }

    dispatch(getJourneys())

    return () => {
      dispatch(reset())
    }
  }, [navigate, user, dispatch])

  // Journey overview/add new journey tab split
  const [isNewJourneyOpen, setIsNewJourneyOpen] = useState(false)

  useEffect(() => { // New journey open if no journeys
    if (!journeys)
      return
    
    if (journeys.length === 0) {
      setIsNewJourneyOpen(true)
    }
  }, [journeys, isSuccess])

  // Success/error on journey manipulation
  useEffect(() => {
    if (actionType === 'get')
        return
  
    let toastTitle = ''
    let toastStatus = ''
  
    if (isSuccess) {
      switch (actionType) {
        case 'create':
          toastTitle = 'Journey created successfully!'
          toastStatus = 'success'
          break
        case 'edit':
          toastTitle = 'Journey edited successfully!'
          toastStatus = 'success'
          break
        case 'delete':
          toastTitle = 'Journey deleted successfully.'
          toastStatus = 'info'
          break
        case 'duplicate':
          toastTitle = 'Journey duplicated successfully!'
          toastStatus = 'success'
          break
        default:
          toastTitle = 'Success!'
          toastStatus = 'success'
      }
  
      toast({
        title: toastTitle,
        status: toastStatus,
        duration: 4000,
        isClosable: true,
        position: "top-right"
      })
    }
  
    if (isError) {
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 8000,
        isClosable: true,
      })
    }
  }, [isSuccess, isError, actionType, journeys, toast])

  // Handle session expiry, preferably move somewhere else at some point so it also checks all other
  useEffect(() => {
    if (message === 'Session expired') {

      dispatch(logoutUser())
      dispatch(resetAuth())
      navigate('/login?sessionExpired=true')

      return
    }
  }, [isError, message])

  const MyHelmet = () => {
    return <Helmet>
      <title>My journeys (Beta) | TrainLookout</title>
      <meta name="description" content="View a record of your rail journeys" />
      <meta name="keywords" content="rail, journey, record, history, train, trainlookout, train lookout, trainlookout.com, train lookout.com" />
      <link rel="canonical" href="/myjourneys" />
    </Helmet>
  }
  
  if (!isSuccess && !isError && actionType === 'get') { // This is probably to change
    return (
      <>
        <MyHelmet />
        <LoadingOverlay type="full" text="Loading journeys..."/>
      </>
    )
  }

    if (isError && actionType === 'get') {
    return (
      <>
      <MyHelmet />
        <StatusOverlay type="modal" text={`My journeys is currently inaccessible. Please try again later. Error details: ${message}`}/>
      </>
    )
  }
    
  return (
    <>
      <MyHelmet />

    <OnboardingViews />
    

    { (user.details.subscription.tier === 'free-restricted' && journeys.length !== 0 && !hasLimitedValiditySubscription(user)) && (
        <SubscriptionExpiredBanner />
    )}

    <MapWithSidebarLayout
        mapComponent={
            <JourneyMap journeys={journeysToDisplay} previewPoints={previewPoints} />
        }
        sidebarComponent={
            <>
                <Stack
                    direction='row'
                    spacing={0}
                    alignSelf='center'
                    borderWidth='1px'
                    shadow='md'
                >
                    <Button
                        variant={isNewJourneyOpen ? 'ghost' : 'solid'}
                        onClick={() => setIsNewJourneyOpen(false)}
                        borderWidth='1px'
                        borderRadius='0'
                    >
                        Overview
                    </Button>
                    <Button
                        variant={isNewJourneyOpen ? 'solid' : 'ghost'}
                        onClick={() => setIsNewJourneyOpen(true)}
                        borderWidth='1px'
                        borderRadius='0'
                    >
                        New journey
                    </Button>
                </Stack>
                {isNewJourneyOpen ? (
                    <JourneyForm setPreviewPoints={setPreviewPoints} />
                ) : (
                    <JourneyOverview journeysToDisplay={journeysToDisplay} />
                )}
            </>
        }
    />

        <Stack direction='row' align='center'>
          <JourneyFilter journeys={journeys} setJourneysToDisplay={setJourneysToDisplay}/>
          <ViewInsightsButton
            isDisabled={journeys.length === 0}
            rightIcon={<MdFiberNew color='red'/>}
          />
        </Stack>

        <JourneyTable journeys={journeysToDisplay}/>
    
    </>
  )
}

export default MyJourneys